import { cn } from '@/lib/utils';
import { ChevronRight, Folder, FolderPlus } from 'lucide-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
  ChainlitContext,
  ClientError,
  ThreadHistory,
  threadHistoryState,
  useChatInteract,
  useChatMessages,
  useChatSession,
  useAuth
} from '@chainlit/react-client';

import Alert from '@/components/Alert';
import { Loader } from '@/components/Loader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem
} from '@/components/ui/sidebar';

import api from '@/api/api';
import { Translator } from '../i18n';
import ThreadOptions from './ThreadOptions';
import { Pencil } from '@/components/icons/Pencil';
import {
  CATEGORIES_ENDPOINT,
  THREAD_CATEGORIES_ENDPOINT,
  THREAD_CATEGORIES_BATCH_ENDPOINT,
  THREAD_CATEGORIES_RENAME_ENDPOINT
} from '@/lib/constants';

interface ThreadListProps {
  threadHistory?: ThreadHistory;
  error?: string;
  isFetching: boolean;
  isLoadingMore: boolean;
}

export function ThreadList({
  threadHistory,
  error,
  isFetching,
  isLoadingMore
}: ThreadListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { idToResume } = useChatSession();
  const { clear } = useChatInteract();
  const { threadId: currentThreadId } = useChatMessages();
  const [threadIdToDelete, setThreadIdToDelete] = useState<string>();
  const [threadIdToRename, setThreadIdToRename] = useState<string>();
  const [threadNewName, setThreadNewName] = useState<string>();
  const [threadNewCategory, setThreadNewCategory] = useState<string>('');
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryToRename, setCategoryToRename] = useState<string>();
  const [categoryNewName, setCategoryNewName] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const setThreadHistory = useSetRecoilState(threadHistoryState);
  const apiClient = useContext(ChainlitContext);
  const { user } = useAuth();

  // Group threads by category
  const categoryGroupedThreads = useMemo(() => {
    if (!threadHistory?.threads) return {};

    const grouped: Record<string, any[]> = {};

    threadHistory.threads.forEach((thread: any) => {
      const category = thread.categories?.[0];

      if (!category) {
        // Threads without categories go to "Uncategorized"
        if (!grouped['Uncategorized']) {
          grouped['Uncategorized'] = [];
        }
        grouped['Uncategorized'].push(thread);
      } else {
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(thread);
      }
    });

    return grouped;
  }, [threadHistory?.threads]);

  const sortedCategoryKeys = useMemo(() => {
    const keys = Object.keys(categoryGroupedThreads);
    // Sort alphabetically, but put "Uncategorized" at the end
    return keys.sort((a, b) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });
  }, [categoryGroupedThreads]);

  const categorizedKeys = useMemo(() => {
    return sortedCategoryKeys.filter((k) => k !== 'Uncategorized');
  }, [sortedCategoryKeys]);

  const uncategorizedThreads = useMemo(() => {
    return categoryGroupedThreads['Uncategorized'] || [];
  }, [categoryGroupedThreads]);

  // Auto-expand current thread's category
  useEffect(() => {
    const threadId = currentThreadId || idToResume;
    if (threadId && threadHistory?.threads) {
      const thread = threadHistory.threads.find((t) => t.id === threadId);
      const category = (thread as any)?.categories?.[0] || 'Uncategorized';
      if (category !== 'Uncategorized' && !expandedCategories[category]) {
        setExpandedCategories((prev) => ({ ...prev, [category]: true }));
      }
    }
  }, [currentThreadId, idToResume, threadHistory?.threads]);

  // Fetch categories when dialog opens
  useEffect(() => {
    if (threadIdToRename) {
      fetchAvailableCategories();
      fetchThreadCategories(threadIdToRename);
    }
  }, [threadIdToRename]);

  if (isFetching || (!threadHistory?.threads && isLoadingMore)) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error" className="m-3">
        {error}
      </Alert>
    );
  }

  if (
    !threadHistory ||
    !threadHistory.threads ||
    threadHistory.threads.length === 0
  ) {
    return (
      <Alert variant="info" className="m-3">
        <Translator path="threadHistory.sidebar.empty" />
      </Alert>
    );
  }

  const handleDeleteThread = () => {
    if (!threadIdToDelete) return;
    if (
      threadIdToDelete === idToResume ||
      threadIdToDelete === currentThreadId
    ) {
      clear();
    }

    toast.promise(apiClient.deleteThread(threadIdToDelete), {
      loading: (
        <Translator path="threadHistory.thread.actions.delete.inProgress" />
      ),
      success: () => {
        setThreadHistory((prev) => ({
          ...prev,
          threads: prev?.threads?.filter((t) => t.id !== threadIdToDelete)
        }));
        navigate('/');
        return (
          <Translator path="threadHistory.thread.actions.delete.success" />
        );
      },
      error: (err) => {
        if (err instanceof ClientError) {
          return <span>{err.message}</span>;
        } else {
          return <span></span>;
        }
      }
    });
  };

  const fetchAvailableCategories = async () => {
    if (!user?.id) {
      console.error('User id not found, cannot fetch categories');
      return;
    }

    setLoadingCategories(true);
    try {
      const data = await api.get<{ categories: string[] }>(
        `${CATEGORIES_ENDPOINT}?user_id=${user.id}`
      );
      setAvailableCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchThreadCategories = async (threadId: string) => {
    try {
      const data = await api.get<{ categories: string[] }>(
        THREAD_CATEGORIES_ENDPOINT.replace(':threadId', threadId)
      );
      const categories = data.categories || [];
      setThreadNewCategory(categories.length > 0 ? categories[0] : '');
    } catch (error) {
      console.error('Error fetching thread categories:', error);
    }
  };

  const handleRenameThread = async () => {
    if (!threadIdToRename || !threadNewName) return;

    const renamePromise = apiClient.renameThread(
      threadIdToRename,
      threadNewName
    );

    // Also update categories if a category is selected
    let categoryPromise: Promise<any> | null = null;
    const threadCatsUrl = `${THREAD_CATEGORIES_ENDPOINT.replace(':threadId', threadIdToRename)}?user_id=${user?.id}`;

    categoryPromise = api.put(threadCatsUrl, {
      categories: threadNewCategory ? [threadNewCategory] : []
    });

    toast.promise(
      Promise.all([renamePromise, categoryPromise].filter(Boolean)),
      {
        loading: (
          <Translator path="threadHistory.thread.actions.rename.inProgress" />
        ),
        success: () => {
          setThreadNewName(undefined);
          setThreadNewCategory('');
          setThreadIdToRename(undefined);
          setThreadHistory((prev) => {
            const next = {
              ...prev,
              threads: prev?.threads ? [...prev.threads] : undefined
            };
            const threadIndex = next.threads?.findIndex(
              (t) => t.id === threadIdToRename
            );
            if (typeof threadIndex === 'number' && next.threads) {
              next.threads[threadIndex] = {
                ...next.threads[threadIndex],
                name: threadNewName,
                categories: threadNewCategory ? [threadNewCategory] : []
              } as any;
            }
            return next;
          });
          return (
            <div>
              <Translator path="threadHistory.thread.actions.rename.success" />
            </div>
          );
        },
        error: (err) => {
          if (err instanceof ClientError) {
            return <span>{err.message}</span>;
          } else {
            return <span></span>;
          }
        }
      }
    );
  };

  const getCategoryLabel = (category: string) => {
    return category;
  };

  const handleRenameCategory = async () => {
    if (
      !categoryToRename ||
      !categoryNewName ||
      categoryToRename === categoryNewName
    ) {
      return;
    }

    const renameCategoryFunc = async () => {
      if (!user?.id) {
        throw new Error('User not found, cannot rename category');
      }

      const data = await api.put<{ detail?: string }>(
        `${THREAD_CATEGORIES_RENAME_ENDPOINT}?user_id=${user.id}`,
        {
          old_category: categoryToRename,
          new_category: categoryNewName
        }
      );

      const oldCategory = categoryToRename;
      const newCategory = categoryNewName;

      setCategoryToRename(undefined);
      setCategoryNewName('');

      // Update thread history to reflect the category rename
      setThreadHistory((prev) => {
        if (!prev?.threads) return prev;

        const updatedThreads = prev.threads.map((thread: any) => {
          const categories = thread.categories || [];
          if (categories.includes(oldCategory)) {
            return {
              ...thread,
              categories: categories.map((cat: string) =>
                cat === oldCategory ? newCategory : cat
              )
            };
          }
          return thread;
        });

        return {
          ...prev,
          threads: updatedThreads
        };
      });

      return 'Category renamed successfully';
    };

    toast.promise(renameCategoryFunc(), {
      loading: 'Renaming category...',
      success: (message) => message as string,
      error: (err) => {
        return (err.message || 'Failed to rename category') as string;
      }
    });
  };

  return (
    <>
      <AlertDialog
        open={!!threadIdToDelete}
        onOpenChange={() => setThreadIdToDelete(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Translator path="threadHistory.thread.actions.delete.title" />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Translator path="threadHistory.thread.actions.delete.description" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0">
              <Translator path="common.actions.cancel" />
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteThread}>
              <Translator path="common.actions.confirm" />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={!!threadIdToRename}
        onOpenChange={(open) => {
          if (!open) {
            setThreadIdToRename(undefined);
            setThreadNewName(undefined);
            setThreadNewCategory('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Edit thread
            </DialogTitle>
            <DialogDescription>
              <Translator path="threadHistory.thread.actions.rename.description" />
            </DialogDescription>
          </DialogHeader>
          <div className="my-6 space-y-4">
            <div>
              <Label htmlFor="name" className="text-right">
                <Translator path="threadHistory.thread.actions.rename.form.name.label" />
              </Label>
              <Input
                id="name"
                required
                value={threadNewName || ''}
                onChange={(e) => setThreadNewName(e.target.value)}
                placeholder={t(
                  'threadHistory.thread.actions.rename.form.name.placeholder'
                )}
                autoFocus
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="category" className="text-right">
                Category
              </Label>
              <Select
                value={threadNewCategory}
                onValueChange={setThreadNewCategory}
                disabled={loadingCategories}
              >
                <SelectTrigger className="mt-2" id="category">
                  <div className="max-w-[26rem] overflow-hidden text-ellipsis whitespace-nowrap">
                    <SelectValue placeholder="Select a category (optional)" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {availableCategories.map((category) => (
                    <div
                      key={category}
                      className="max-w-[28rem] overflow-hidden text-ellipsis whitespace-nowrap"
                    >
                      <SelectItem value={category}>{category}</SelectItem>
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setThreadIdToRename(undefined);
                setThreadNewName(undefined);
                setThreadNewCategory('');
              }}
            >
              <Translator path="common.actions.cancel" />
            </Button>
            <Button type="button" onClick={handleRenameThread}>
              <Translator path="common.actions.confirm" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!categoryToRename}
        onOpenChange={(open) => {
          if (!open) {
            setCategoryToRename(undefined);
            setCategoryNewName('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Category</DialogTitle>
            <DialogDescription className="break-all">
              Rename the category "{categoryToRename}". This will update all
              threads in this category.
            </DialogDescription>
          </DialogHeader>
          <div className="my-6 space-y-4">
            <div>
              <Label htmlFor="category-name" className="text-right">
                Category Name
              </Label>
              <Input
                id="category-name"
                required
                value={categoryNewName}
                onChange={(e) => setCategoryNewName(e.target.value)}
                placeholder="Enter new category name"
                autoFocus
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCategoryToRename(undefined);
                setCategoryNewName('');
              }}
            >
              <Translator path="common.actions.cancel" />
            </Button>
            <Button
              type="button"
              onClick={handleRenameCategory}
              disabled={
                !categoryNewName || categoryNewName === categoryToRename
              }
            >
              <Translator path="common.actions.confirm" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {categorizedKeys.length > 0 && (
        <SidebarGroup>
          <SidebarGroupLabel>Categories</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {categorizedKeys.map((category) => {
                const items = categoryGroupedThreads[category];
                const isExpanded = !!expandedCategories[category];

                return (
                  <SidebarMenuItem key={category}>
                    <SidebarMenuButton
                      onClick={() =>
                        setExpandedCategories((prev) => ({
                          ...prev,
                          [category]: !prev[category]
                        }))
                      }
                      className="h-9 w-full group/folder"
                    >
                      <Folder className="h-4 w-4 shrink-0 text-sidebar-foreground/70" />
                      <span className="truncate flex-1 font-medium">
                        {getCategoryLabel(category)}
                      </span>
                      <ChevronRight
                        className={cn(
                          'h-4 w-4 transition-transform text-sidebar-foreground/50',
                          isExpanded && 'rotate-90'
                        )}
                      />
                    </SidebarMenuButton>

                    <SidebarMenuAction
                      onClick={(e) => {
                        e.stopPropagation();
                        setCategoryToRename(category);
                        setCategoryNewName(category);
                      }}
                      showOnHover
                    >
                      <Pencil className="h-3 w-3" />
                    </SidebarMenuAction>

                    {isExpanded && (
                      <SidebarMenuSub>
                        {items?.map((thread: any) => {
                          const isResumed =
                            idToResume === thread.id &&
                            !threadHistory!.currentThreadId;
                          const isSelected =
                            isResumed ||
                            threadHistory!.currentThreadId === thread.id;
                          return (
                            <SidebarMenuSubItem key={thread.id}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={isSelected}
                                className="group/thread relative h-8"
                              >
                                <Link
                                  to={isResumed ? '' : `/thread/${thread.id}`}
                                >
                                  <span className="truncate">
                                    {thread.name || (
                                      <Translator path="threadHistory.thread.untitled" />
                                    )}
                                  </span>
                                  <ThreadOptions
                                    onDelete={() =>
                                      setThreadIdToDelete(thread.id)
                                    }
                                    onRename={() => {
                                      setThreadIdToRename(thread.id);
                                      setThreadNewName(thread.name || '');
                                    }}
                                    onMove={() => {
                                      setThreadIdToRename(thread.id);
                                      setThreadNewName(thread.name || '');
                                    }}
                                    className={cn(
                                      'absolute z-20 bottom-0 top-0 right-0 hover:text-primary flex opacity-0 group-hover/thread:opacity-100 items-center px-1',
                                      isSelected && 'opacity-100'
                                    )}
                                  />
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {uncategorizedThreads.length > 0 && (
        <SidebarGroup>
          <SidebarGroupLabel>Recent</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {uncategorizedThreads.map((thread: any) => {
                const isResumed =
                  idToResume === thread.id && !threadHistory!.currentThreadId;
                const isSelected =
                  isResumed || threadHistory!.currentThreadId === thread.id;
                return (
                  <SidebarMenuItem key={thread.id} id={`thread-${thread.id}`}>
                    <Link to={isResumed ? '' : `/thread/${thread.id}`}>
                      <SidebarMenuButton
                        isActive={isSelected}
                        className="relative truncate h-9 group/thread"
                      >
                        {thread.name || (
                          <Translator path="threadHistory.thread.untitled" />
                        )}
                        <ThreadOptions
                          onDelete={() => setThreadIdToDelete(thread.id)}
                          onRename={() => {
                            setThreadIdToRename(thread.id);
                            setThreadNewName(thread.name || '');
                          }}
                          onMove={() => {
                            setThreadIdToRename(thread.id);
                            setThreadNewName(thread.name || '');
                          }}
                          className={cn(
                            'absolute z-20 bottom-0 top-0 right-0 bg-sidebar-accent hover:bg-sidebar-accent hover:text-primary flex opacity-0 group-hover/thread:opacity-100 items-center px-1',
                            isSelected && 'bg-sidebar-accent opacity-100'
                          )}
                        />
                      </SidebarMenuButton>
                    </Link>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {isLoadingMore ? (
        <div className="flex items-center justify-center p-2">
          <Loader />
        </div>
      ) : null}
    </>
  );
}
